import { useMemo } from "react";
import { DISPLAY_DATE_WITH_TIME } from "@constants/date-formats";
import { resources } from "@constants/hardcoded-strings";
import { TableSettingsTableTypes } from "@enums/table-settings-table-types";
import { useTableFilters } from "@hooks/use-table-filters";
import { FormattedActivityLog } from "@idw-types/data/reports";
import { formatDateToString, sortDates } from "@utils/date-util";
import { sortStrings } from "@utils/string-util";
import { IdwTable } from "@containers/common/idw-table/idw-table";
import { PaginationContainer } from "@containers/common/pagination-container/pagination-container";
import { IdwTableContainer } from "@containers/common/idw-table-container/idw-table-container";
import styles from "./activity-log-table-container.less";
import { createColumnHelper, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, TableState, useReactTable } from "@tanstack/react-table";
import { useDispatch } from "react-redux";
import { handleSetColumnFiltersChange, handleSetPaginationChange, handleSetSortingChange } from "@utils/react-table/react-table-util";

type ActivityLogTableContainerProps = {
    activityLogs: Array<FormattedActivityLog>;
    tableState: TableState;
    isPageHeight: boolean;
    tableSettingsTableType?: TableSettingsTableTypes;
};

type ActivityLogTableDataRow = FormattedActivityLog & {
    byUser: string;
};

export const ActivityLogTableContainer = (props: ActivityLogTableContainerProps) => {

    const {
        activityLogs,
        tableState,
        isPageHeight,
        tableSettingsTableType
    } = props;

    const dispatch = useDispatch();

    const activityLogTableData: Array<ActivityLogTableDataRow> = useMemo(() => {
        return activityLogs.map(activityLog => {

            if (activityLog.axnAdminUser) {
                const user = activityLog.axnAdminUser;
                let byUser = "";
                if (user.firstName) {
                    byUser += `${user.firstName} `;
                }
                if (user.lastName) {
                    byUser += `${user.lastName} `;
                }
                if (user.email) {
                    byUser += `(${user.email})`;
                }
                return {
                    ...activityLog,
                    byUser: byUser
                };
            }

            return {
                ...activityLog,
                byUser: "--"
            };
        });
    }, [activityLogs]);

    const columnHelper = createColumnHelper<ActivityLogTableDataRow>();
    const columns = useMemo(() => ([
        columnHelper.accessor("label", {
            header: resources.tables.activityLog.columns.activityType.header,
            filterFn: "fuzzyText",
            sortingFn: (a, b) => sortStrings(a.original.label, b.original.label)
        }),
        columnHelper.accessor("action", {
            header: resources.tables.activityLog.columns.action.header,
            filterFn: "fuzzyText"
        }),
        columnHelper.accessor("byUser", {
            header: resources.tables.activityLog.columns.byUser.header,
            filterFn: "fuzzyText",
            sortingFn: (a, b) => sortStrings(a.original.byUser, b.original.byUser)
        }),
        columnHelper.accessor("description", {
            header: resources.tables.activityLog.columns.description.header,
            filterFn: "fuzzyText"
        }),
        columnHelper.accessor("date", {
            header: resources.tables.activityLog.columns.time.header,
            cell: (props) => props.getValue() ? <span>{formatDateToString(props.getValue(), DISPLAY_DATE_WITH_TIME)}</span> : null,
            sortingFn: (a, b) => sortDates(a.original.date, b.original.date),
            filterFn: "dateTimeFilter"
        })
    ]), [activityLogTableData]);

    const { filterTypes } = useTableFilters();

    const useTableResult = useReactTable({
        columns,
        data: activityLogTableData,
        state: tableState,
        filterFns: filterTypes,
        sortDescFirst: false,
        autoResetPageIndex: true,
        onSortingChange: (sortingOrUpdateFn) => handleSetSortingChange(sortingOrUpdateFn, tableSettingsTableType, tableState.sorting, dispatch),
        onColumnFiltersChange: (filtersOrUpdateFn) => handleSetColumnFiltersChange(filtersOrUpdateFn, tableSettingsTableType, tableState.columnFilters, dispatch),
        onPaginationChange: (paginationOrUpdateFn) => handleSetPaginationChange(paginationOrUpdateFn, tableSettingsTableType, tableState.pagination, dispatch),
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel()
    });

    return (
        <IdwTableContainer className={styles.activityLogTableContainer}>
            <IdwTable
                id="activity-log"
                useTableResult={useTableResult}
                className={styles.activityLogTable}
                headerColumnKeyPrefix={"activity-log"}
            />
            <PaginationContainer
                idPrefix="activity-log"
                useTableResult={useTableResult}
                isPageHeight={isPageHeight}
            />
        </IdwTableContainer>
    );
};